import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../Context/UserContext';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    InputAdornment,
    Avatar
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    People as PeopleIcon,
    Group as GroupIcon
} from '@mui/icons-material';

const mockClientGroups = [
    {
        id: 1,
        name: 'Entreprises Premium',
        description: 'Grandes entreprises avec abonnements premium',
        clientsCount: 45,
        color: '#10b981',
        totalRevenue: 15650,
        averageSubscription: 'Premium'
    },
    {
        id: 2,
        name: 'Particuliers',
        description: 'Clients particuliers individuels',
        clientsCount: 127,
        color: '#3b82f6',
        totalRevenue: 3850,
        averageSubscription: 'Basic'
    },
    {
        id: 3,
        name: 'Clients Test',
        description: 'Clients utilisés pour les tests',
        clientsCount: 8,
        color: '#f59e0b',
        totalRevenue: 0,
        averageSubscription: 'Test'
    },
    {
        id: 4,
        name: 'Équipe Interne',
        description: 'Membres de l\'équipe interne',
        clientsCount: 12,
        color: '#8b5cf6',
        totalRevenue: 0,
        averageSubscription: 'Interne'
    },
    {
        id: 5,
        name: 'PME',
        description: 'Petites et moyennes entreprises',
        clientsCount: 89,
        color: '#ef4444',
        totalRevenue: 8920,
        averageSubscription: 'Standard'
    },
    {
        id: 6,
        name: 'Startups',
        description: 'Jeunes entreprises en développement',
        clientsCount: 34,
        color: '#06b6d4',
        totalRevenue: 2040,
        averageSubscription: 'Startup'
    }
];

const ClientGroupsListPage = () => {
    const navigate = useNavigate();
    const { userInfo } = useContext(UserContext);
    const canManageGroups = userInfo?.is_super_admin === true || (userInfo?.permissions || []).includes('client-groups.manage');
    const [clientGroups, setClientGroups] = useState(mockClientGroups);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredGroups = clientGroups.filter(group =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalClients = clientGroups.reduce((total, group) => total + group.clientsCount, 0);
    const totalRevenue = clientGroups.reduce((total, group) => total + group.totalRevenue, 0);

    const handleAddGroup = () => {
        navigate('/client-groups/new');
    };

    const handleEditGroup = (groupId) => {
        navigate(`/client-groups/${groupId}/edit`);
    };

    const handleDeleteGroup = (groupId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe de clients ?')) {
            setClientGroups(prev => prev.filter(group => group.id !== groupId));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        Groupes de clients
                    </Typography>
                </Box>
                {canManageGroups && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddGroup}
                    >
                        Nouveau groupe
                    </Button>
                )}
            </Box>

            {/* Statistiques */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#3b82f6', mb: 1 }}>
                                {clientGroups.length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Groupes total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#10b981', mb: 1 }}>
                                {totalClients}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Clients total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f59e0b', mb: 1 }}>
                                {Math.round(totalClients / clientGroups.length)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Moyenne/groupe
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#8b5cf6', mb: 1 }}>
                                {totalRevenue.toLocaleString()}€
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Revenus total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent sx={{ p: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Rechercher un groupe de clients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 3 }}
                    />

                    <Grid container spacing={3}>
                        {filteredGroups.map((group) => (
                            <Grid item xs={12} sm={6} md={4} key={group.id}>
                                <Card
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                            borderColor: group.color
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Chip
                                                label={group.name}
                                                sx={{
                                                    bgcolor: group.color,
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                            {canManageGroups && (
                                                <Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditGroup(group.id)}
                                                        sx={{ color: 'text.secondary', mr: 1 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteGroup(group.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>

                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                            {group.description}
                                        </Typography>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PeopleIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {group.clientsCount} client(s)
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={group.averageSubscription}
                                                size="small"
                                                variant="outlined"
                                                sx={{ borderColor: group.color, color: group.color }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Revenus: {group.totalRevenue.toLocaleString()}€
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {filteredGroups.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <GroupIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                                Aucun groupe trouvé
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                {searchTerm ? 'Essayez de modifier votre recherche' : 'Commencez par créer votre premier groupe de clients'}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        Vue d'ensemble détaillée
                    </Typography>
                    <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <Table>
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Groupe</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Clients</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Abonnement moyen</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Revenus</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredGroups.map((group) => (
                                    <TableRow key={group.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Avatar sx={{ bgcolor: group.color, mr: 2, width: 32, height: 32 }}>
                                                    <GroupIcon fontSize="small" />
                                                </Avatar>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {group.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                            {group.description}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {group.clientsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={group.averageSubscription}
                                                size="small"
                                                sx={{
                                                    bgcolor: group.color,
                                                    color: 'white',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {group.totalRevenue.toLocaleString()}€
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {canManageGroups && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditGroup(group.id)}
                                                        sx={{ color: 'text.secondary', mr: 1 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteGroup(group.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ClientGroupsListPage;